import React from 'react';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { useNavigation, useRoute } from '@react-navigation/native';
import { styled } from 'dripsy';
import { Platform } from 'react-native';

import {
  ApplePaySvg,
  BaseText,
  Button,
  GooglePaySvg,
  PlanPriceView,
  Separator,
  SettingsLayout,
} from '@components';
import { useNavOptionsSet } from '@hooks';
import {
  ProfileNavigatorNavigationPropType,
  ProfileNavigatorRoutePropType,
  screens,
  ScreensNameType,
} from '@navigation';
import { useViewerModel } from '@models/Viewer/ViewerModel';
import { IS_WEB, parseApiError } from '@utils';
import { ToastService } from '@services';

const Subtitle = styled(BaseText)({
  textAlign: IS_WEB ? 'center' : 'left',
  variant: 'text.xlarge',
  color: '$heading800',
  fontWeight: 'semi',
});

const Description = styled(BaseText)({
  px: IS_WEB ? '$8' : '$0',
  textAlign: IS_WEB ? 'center' : 'left',
  variant: 'text.medium',
  color: '$text400',
});

const ServicePayButton = styled(Button)({
  bg: '$black',
});

const ServicePayText = styled(BaseText)({
  color: '$background0',
  fontWeight: 'semi',
  variant: 'text.xlarge',
  mr: '$2',
});

const ServicePaySvg = Platform.select({
  android: GooglePaySvg,
  default: ApplePaySvg,
});

const BREADCRUMB_ROUTES: ScreensNameType[] = [
  screens.Profile,
  screens.Settings,
  screens.Subscription,
  screens.ChangeSubscriptionPlan,
  screens.Payment,
];

type RoutePropType = ProfileNavigatorRoutePropType<'Payment'>;
type NavigationPropType =
  ProfileNavigatorNavigationPropType<'Payment'>;

export const PaymentScreen = observer(() => {
  const { t } = useTranslation();
  const route = useRoute<RoutePropType>();
  const viewer = useViewerModel();
  const nav = useNavigation<NavigationPropType>();

  useNavOptionsSet({ title: t('payment.title') });

  async function changeTariffPlan() {
    try {
      // TODO: request IAP
      await viewer.currentUser?.tariffPlan.changeTariffPlan.run({
        tariffPlan: route.params.tariffPlan,
      });

      ToastService.showSuccess(t('payment.successMessage'));

      nav.navigate(screens.Subscription);
    } catch (error) {
      ToastService.showError(parseApiError(error).message);
    }
  }

  const tKeyForPlan = `common.subscriptionPlans.${route.params.tariffPlan}`;
  const plan = viewer.tariffPlans.byCode[route.params.tariffPlan];
  const formattedCurrentPrice = plan?.price.formattedCurrent ?? '';
  const formattedRegularPrice = plan?.price.hasDiscount
    ? plan?.price.formattedRegular
    : '';

  const formattedAutoupgradePrice =
    plan?.autoupgradePlan?.price.formattedRegular ?? '';

  const subtitle = t(`${tKeyForPlan}.subtitle`, {
    price: formattedAutoupgradePrice,
    defaultValue: null,
  });

  return (
    <SettingsLayout
      breadcrumbRoutes={BREADCRUMB_ROUTES}
      title={t('payment.title')}
    >
      <Subtitle>{t('payment.subtitle')}</Subtitle>
      <Separator size="$4" />
      <Description>{t('payment.description')}</Description>

      <Separator size="$8" />

      <PlanPriceView
        currentPrice={formattedCurrentPrice}
        regularPrice={formattedRegularPrice}
        title={t(`${tKeyForPlan}.title`)}
        subtitle={subtitle}
      />
      <Separator />

      <ServicePayButton
        isTextChildren={false}
        onPress={changeTariffPlan}
        isLoading={
          viewer.currentUser?.tariffPlan.changeTariffPlan.inProgress
        }
      >
        <ServicePayText>
          {t('payment.pay', { context: Platform.OS })}
        </ServicePayText>
        <ServicePaySvg />
      </ServicePayButton>
    </SettingsLayout>
  );
});
